<script setup>
import { computed, ref, onMounted } from 'vue';
import NoDataAutocomplete from '@/components/widgets/NoDataAutocomplete.vue';
import { debounce } from 'lodash';

const props = defineProps({
    modelValue: {},
    search: {},
    items: {},
    loading: {},
    disabled: {},
    showRetryButton: {},
    readonly: {},
    multiple: {},
    label: {},
    itemTitle: {},
    itemValue: {},
    clearable: {
        type: Boolean,
        default: true
    },
    debounceTime: {
        type: Number,
        default: 300 // Default debounce time in milliseconds
    },
    infiniteScroll: {
        type: Boolean,
        default: false
    },
    showOptionsButton: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits([
    'update:modelValue',
    'update:search',
    'update:focused',
    'click:clear',
    'searching',
    'retry',
    'update',
    'preparingSearch',
    'search',
    'infinite-scroll',
    'open-dialog'
]);

const selected = computed({
    get() {
        return props.modelValue;
    },
    set(value) {
        emit('update:modelValue', value);
    }
});

const internalSearchQuery = ref('');

// Create debounced search function
const debouncedSearch = debounce((value) => {
    emit('update:search', value);
    if (value && value.length > 0) {
        emit('search', value);
    }
}, props.debounceTime);

// Computed property for search query
const searchQuery = computed({
    get() {
        return internalSearchQuery.value;
    },
    set(value) {
        internalSearchQuery.value = value;
        debouncedSearch(value);
    }
});

// Initialize internal search query with props value
onMounted(() => {
    internalSearchQuery.value = props.search || '';
});

const autocomplete = ref(null);
const removeFocus = () => {
    autocomplete.value.blur();
};

const onClearPressed = () => {
    internalSearchQuery.value = '';
    emit('update:search', '');
    emit('search', '');
};

// Handle search input
const handleSearch = (event) => {
    searchQuery.value = event.target.value;
};

// Add the missing computed property for infinite scroll
const infiniteScrollDisabled = computed(() => {
    return props.loading || !props.infiniteScroll;
});
</script>

<template>
    <v-autocomplete
        ref="autocomplete"
        v-model="selected"
        :search="internalSearchQuery"
        :items="items"
        :item-title="itemTitle"
        :item-value="itemValue"
        :label="label"
        :multiple="multiple"
        hide-details
        density="compact"
        persistent-clear
        :disabled="disabled"
        :readonly="readonly"
        :clearable="clearable"
        @update:model-value="
            emit('update', selected);
            removeFocus();
        "
        :loading="loading"
        no-filter
        @update:focused="(state) => emit('update:focused', state)"
        @click:clear="onClearPressed"
        @keyup="handleSearch"
    >
        <template v-slot:item="{ props, item }">
            <slot :props="props" :item="item"></slot>
        </template>

        <template v-slot:append-item>
            <div v-if="infiniteScroll">
                <v-infinite-scroll
                    @load="(e) => emit('infinite-scroll', e)"
                    :disabled="infiniteScrollDisabled"
                />
            </div>
        </template>

        <template v-slot:prepend-item>
            <slot name="prepend-item"></slot>
        </template>

        <template v-if="$slots.selection" v-slot:selection="{ item }">
            <slot name="selection" :item="item"></slot>
        </template>

        <template v-if="$slots.append" v-slot:append>
            <slot name="append"></slot>
        </template>

        <template v-if="$slots.prepend" v-slot:prepend>
            <slot name="prepend"></slot>
        </template>

        <template v-if="showOptionsButton && !readonly" v-slot:append-inner>
            <v-btn
                class="ml-1 options-btn"
                icon="mdi-dots-horizontal"
                variant="text"
                density="compact"
                @click.stop="emit('open-dialog')"
            />
        </template>

        <template v-slot:no-data>
            <NoDataAutocomplete
                :loading="loading"
                :show-retry-button="showRetryButton"
                @retry="emit('retry')"
            ></NoDataAutocomplete>
        </template>
    </v-autocomplete>
</template>

<style lang="scss" scoped>
:deep(.v-autocomplete__selection) {
    margin-inline-end: 0px !important;
}

.no-more-results {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 16px 0 8px 0;
    width: 100%;
}
</style>
